import { Component, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

// ─────────────────────── types ────────────────────────────
export type activity_action =
  | 'login' | 'logout' | 'login_failed' | 'create' | 'update' | 'delete'
  | 'view' | 'export' | 'approve' | 'reject' | 'print' | 'download'
  | 'settings' | 'permission';

export type activity_module =
  | 'auth' | 'dashboard' | 'inquery' | 'application' | 'admissions' | 'eligibility' | 'students' | 'staff' 
  | 'attendance' | 'timetable' | 'fees' | 'exams' | 'library' | 'transport' 
  | 'inventory' | 'finance' | 'gate-pass' | 'hr' | 'directory' | 'user' 
  | 'notifications' | 'subscription' | 'settings' | 'reports';

export type activity_severity = 'info' | 'success' | 'warning' | 'danger';
export type activity_status   = 'success' | 'failed';
export type view_mode         = 'timeline' | 'table';

export interface activity_log_entry {
  id:           string;
  cur_date:     string;
  user_id:      string;
  user_name:    string;
  user_role:    string;
  user_email:   string;
  user_avatar:  string;
  act_action:   activity_action;
  act_module:   activity_module;
  description:  string;
  entity_type:  string;
  entity_id:    string;
  entity_label: string;
  severity:     activity_severity;
  status:       activity_status;
  ip_address:   string;
  device:       string;
  browser:      string;
  notes:        string;
}

// ─────────────────────── component ───────────────────────
@Component({
  selector:    'app-activity-log',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './activity-log.html',
  styleUrl:    './activity-log.scss',
})
export class ActivityLog {
  dataService     = inject(DataService);
  loading = false;
  user: any = null;

  readonly module_opts = [
    { value: 'auth',          label: 'Authentication', icon: 'bi-shield-lock' },
    { value: 'dashboard',     label: 'Dashboard',      icon: 'bi-speedometer2' },
    { value: 'admissions',    label: 'Admissions',     icon: 'bi-person-plus' },
    { value: 'eligibility',   label: 'Eligibility',    icon: 'bi-search-heart' },
    { value: 'students',      label: 'Students',       icon: 'bi-people' },
    { value: 'staff',         label: 'Staff',          icon: 'bi-person-badge' },
    { value: 'attendance',    label: 'Attendance',     icon: 'bi-calendar3' },
    { value: 'timetable',     label: 'Timetable',      icon: 'bi-calendar-week' },
    { value: 'fees',          label: 'Fees',           icon: 'bi-cash-coin' },
    { value: 'exams',         label: 'Examinations',   icon: 'bi-trophy' },
    { value: 'library',       label: 'Library',        icon: 'bi-journals' },
    { value: 'transport',     label: 'Transport',      icon: 'bi-bus-front' },
    { value: 'inventory',     label: 'Inventory',      icon: 'bi-boxes' },
    { value: 'finance',       label: 'Finance',        icon: 'bi-cash-stack' },
    { value: 'gate-pass',     label: 'Gate Pass',      icon: 'bi-pass' },
    { value: 'hr',            label: 'HR Admin',       icon: 'bi-people' },
    { value: 'directory',     label: 'Directory',      icon: 'bi-telephone' },
    { value: 'notifications', label: 'Notifications',  icon: 'bi-bell' },
    { value: 'subscription',  label: 'Subscription',   icon: 'bi-patch-check' },
    { value: 'settings',      label: 'Settings',       icon: 'bi-gear' },
    { value: 'reports',       label: 'Reports',        icon: 'bi-bar-chart' },
  ] as const;

  readonly action_opts = [
    { value: 'login',        label: 'Login' },
    { value: 'logout',       label: 'Logout' },
    { value: 'login_failed', label: 'Failed Login' },
    { value: 'create',       label: 'Create' },
    { value: 'update',       label: 'Update' },
    { value: 'delete',       label: 'Delete' },
    { value: 'view',         label: 'View' },
    { value: 'export',       label: 'Export' },
    { value: 'approve',      label: 'Approve' },
    { value: 'reject',       label: 'Reject' },
    { value: 'print',        label: 'Print' },
    { value: 'download',     label: 'Download' },
    { value: 'settings',     label: 'Settings' },
    { value: 'permission',   label: 'Permission' },
  ] as const;

  private readonly mutation_actions: activity_action[] = [
    'create', 'update', 'delete', 'approve', 'reject', 'permission', 'settings',
  ];
  private readonly security_actions: activity_action[] = ['login_failed', 'permission'];

  view_mode      = signal<view_mode>('table');
  page           = signal(1);
  page_size      = signal(10);
  readonly page_size_opts = [5, 10, 15, 20, 25, 50, 100];
  toast_msg      = signal('');
  selected_entry = signal<activity_log_entry | null>(null);

  show_form      = signal(false);
  is_edit        = signal(false);
  editing_id     = signal<string | null>(null);
  form_submitted = false;
  form_errors: Record<string, string> = {};
  entry_form: Partial<activity_log_entry> = {};
  form_timestamp = '';
  delete_target  = signal<activity_log_entry | null>(null);

  readonly device_opts  = ['Desktop', 'Laptop', 'Mobile', 'Tablet', 'Unknown'];
  readonly browser_opts = ['Chrome', 'Chrome 129', 'Edge 128', 'Safari 18', 'Firefox', 'Unknown'];
  readonly role_opts    = ['Super Admin', 'Senior Teacher', 'Accountant', 'HR Manager', 'Librarian', 'Front Desk', 'Transport Incharge', 'Unauthenticated'];

  search_query   = signal('');
  user_id        = signal('');
  module_f       = signal<activity_module | ''>('');
  action_f       = signal<activity_action | ''>('');
  severity_f     = signal<activity_severity | ''>('');
  status_f       = signal<activity_status | ''>('');
  date_from      = signal('');
  date_to        = signal('');
  mutations_only = signal(false);
  security_only  = signal(false);

  logs = signal<activity_log_entry[]>([]);      //(this.build_logs());
  unique_users: any = [];

  // readonly unique_users = computed(() => {
  //   const map = new Map<string, { user_id: string; user_name: string }>();
  //   for (const row of this.logs()) {
  //     if (!map.has(row.user_id)) map.set(row.user_id, { user_id: row.user_id, user_name: row.user_name });
  //   }
  //   return [...map.values()].sort((a, b) => a.user_name.localeCompare(b.user_name));
  // });

  readonly filtered_logs = computed(() => {
    const q         = this.search_query().trim().toLowerCase();
    const user_id   = this.user_id();
    const module_f  = this.module_f();
    const action_f  = this.action_f();
    const severity  = this.severity_f();
    const status    = this.status_f();
    const from_time = this.date_from() ? Date.parse(`${this.date_from()}T00:00:00`) : null;
    const to_time   = this.date_to()   ? Date.parse(`${this.date_to()}T23:59:59`)   : null;
    const extra     = this.mutations_only() ? this.mutation_actions
                    : this.security_only()  ? this.security_actions
                    : null;

    return this.logs().filter(row => {
      if (user_id  && row.user_id  !== user_id)  return false;
      if (module_f && row.act_module   !== module_f) return false;
      if (action_f && row.act_action   !== action_f) return false;
      if (severity && row.severity !== severity) return false;
      if (status   && row.status   !== status)   return false;
      if (extra    && !extra.includes(row.act_action)) return false;

      const ts = Date.parse(row.cur_date);
      if (from_time !== null && ts < from_time) return false;
      if (to_time   !== null && ts > to_time)   return false;

      if (!q) return true;
      const hay = [
        row.description, row.user_name, row.user_email, row.user_role,
        row.act_module, row.act_action, row.entity_label, row.entity_id,
        row.ip_address, row.id, row.notes,
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  });

  readonly stats = computed(() => {
    const list = this.filtered_logs();
    const today = new Date().toISOString().slice(0, 10);
    const users = new Set(list.map(r => r.user_id));
    return {
      total:        list.length,
      today:        list.filter(r => r.cur_date.slice(0, 10) === today).length,
      unique_users: users.size,
      mutations:    list.filter(r => this.mutation_actions.includes(r.act_action)).length,
      security:     list.filter(r => this.security_actions.includes(r.act_action)).length,
      failed:       list.filter(r => r.status === 'failed').length,
    };
  });

  readonly page_data = computed(() => {
    const items       = this.filtered_logs();
    const total       = items.length;
    const size        = this.page_size() === 0 ? Math.max(total, 1) : this.page_size();
    const total_pages = Math.max(1, Math.ceil(total / size));
    const page        = Math.min(Math.max(1, this.page()), total_pages);
    const start       = (page - 1) * size;
    const slice       = items.slice(start, start + size);
    return {
      items: slice,
      total,
      page,
      total_pages,
      from:  total === 0 ? 0 : start + 1,
      to:    start + slice.length,
      size,
    };
  });

  readonly grouped_by_day = computed(() => {
    const groups: { date: string; label: string; items: activity_log_entry[] }[] = [];
    const map = new Map<string, activity_log_entry[]>();
    for (const item of this.page_data().items) {
      const key = item.cur_date.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    for (const [date, items] of map) {
      groups.push({ date, label: this.day_label(date), items });
    }
    return groups;
  });

  readonly has_active_filters = computed(() =>
    !!(this.search_query() || this.user_id() || this.module_f() || this.action_f()
      || this.severity_f() || this.status_f() || this.date_from() || this.date_to()
      || this.mutations_only() || this.security_only()),
  );

  readonly pages = computed(() => {
    const total   = this.page_data().total_pages;
    const current = this.page_data().page;
    const start   = Math.max(1, current - 2);
    const end     = Math.min(total, start + 4);
    const window: number[] = [];
    for (let i = Math.max(1, end - 4); i <= end; i++) window.push(i);
    return window;
  });

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;

      this.unique_users = [{
        user_id: this.user.id, 
        user_name: this.user.display_name,
        user_role: this.user.user_role[0].name,
        user_email: this.user.email_id || '', 
        user_avatar: this.dataService.getInitName(this.user.display_name)
      }];

    }

    this.loading = true;
    await this.dataService.getAllRecords('activity_log').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          if(typeof res[i].user_role == 'string') {
            res[i].user_role = this.dataService.str2json(res[i].user_role)[0].name;
          }
        }
        this.logs.set(res);
      }
    });


  }

  set_view(mode: view_mode): void {
    this.view_mode.set(mode);
  }

  apply_quick(kind: 'today' | 'security' | 'failed' | 'mutations' | 'all'): void {
    this.clear_filters(false);
    const today = new Date().toISOString().slice(0, 10);
    if (kind === 'today') {
      this.date_from.set(today);
      this.date_to.set(today);
    } else if (kind === 'security') {
      this.security_only.set(true);
    } else if (kind === 'failed') {
      this.status_f.set('failed');
    } else if (kind === 'mutations') {
      this.mutations_only.set(true);
    }
    this.page.set(1);
  }

  on_filter_change(): void {
    this.page.set(1);
  }

  clear_filters(reset_page = true): void {
    this.search_query.set('');
    this.user_id.set('');
    this.module_f.set('');
    this.action_f.set('');
    this.severity_f.set('');
    this.status_f.set('');
    this.date_from.set('');
    this.date_to.set('');
    this.mutations_only.set(false);
    this.security_only.set(false);
    if (reset_page) this.page.set(1);
  }

  go_to(page: number): void {
    const last = this.page_data().total_pages;
    this.page.set(Math.min(Math.max(1, page), last));
  }

  go_first(): void { this.go_to(1); }
  go_last(): void  { this.go_to(this.page_data().total_pages); }
  go_prev(): void  { this.go_to(this.page_data().page - 1); }
  go_next(): void  { this.go_to(this.page_data().page + 1); }

  set_page_size(size: number): void {
    this.page_size.set(size);
    this.page.set(1);
  }

  page_size_label(n: number): string {
    return n === 0 ? 'All' : String(n);
  }

  open_detail(entry: activity_log_entry): void {
    this.selected_entry.set(entry);
  }

  close_detail(): void {
    this.selected_entry.set(null);
  }

  open_add(): void {
    const nav = navigator as any;
    this.entry_form = {
      user_name:   this.user.display_name,
      user_role:   this.user.user_role[0].name,
      user_email:  this.user.email_id || '',
      act_action:  'create',
      act_module:  'dashboard',
      description: '',
      entity_type: '',
      entity_id:   '',
      entity_label:'',
      severity:    'success',
      status:      'success',
      ip_address:  this.user.ip_address,
      device:      this.dataService.detectDeviceType(),
      browser:     nav.userAgentData.brands[0].brand,
      cur_date:   this.to_local_input(new Date().toISOString()),
      notes:       '',
    };
    this.form_timestamp = this.to_local_input(new Date().toISOString());
    this.form_errors    = {};
    this.form_submitted = false;
    this.is_edit.set(false);
    this.editing_id.set(null);
    this.show_form.set(true);
  }

  open_edit(entry: activity_log_entry, ev?: Event): void {
    ev?.stopPropagation();
    this.entry_form = { ...entry };
    this.form_timestamp = this.to_local_input(entry.cur_date);
    this.form_errors    = {};
    this.form_submitted = false;
    this.is_edit.set(true);
    this.editing_id.set(entry.id);
    this.selected_entry.set(null);
    this.show_form.set(true);
  }

  close_form(): void {
    this.show_form.set(false);
    this.form_submitted = false;
    this.form_errors    = {};
  }

  save_entry(): void {
    this.form_submitted = true;
    this.form_errors    = {};

    const f = this.entry_form;
    if (!f.user_name?.trim())   this.form_errors['user_name']   = 'User name is required.';
    if (!f.description?.trim()) this.form_errors['description'] = 'Description is required.';
    if (!f.act_action)              this.form_errors['action']      = 'Action is required.';
    if (!f.act_module)              this.form_errors['module']      = 'Module is required.';
    if (!this.form_timestamp)   this.form_errors['timestamp']   = 'Date & time is required.';
    if (Object.keys(this.form_errors).length) return;

    const user_name = f.user_name!.trim();
    const row: activity_log_entry = {
      id:           this.is_edit() && this.editing_id() ? this.editing_id()! : this.next_id(),
      cur_date:     this.from_local_input(this.form_timestamp),
      user_id:      this.user.id || this.slug_id(user_name),
      user_name,
      user_role:    f.user_role?.trim() || 'Staff',
      user_email:   f.user_email?.trim() || '',
      user_avatar:  this.make_avatar(user_name),
      act_action:   f.act_action!,
      act_module:   f.act_module!,
      description:  f.description!.trim(),
      entity_type:  f.entity_type?.trim() || '',
      entity_id:    f.entity_id?.trim() || '',
      entity_label: f.entity_label?.trim() || '',
      severity:     f.severity ?? this.default_severity(f.act_action!),
      status:       f.status ?? 'success',
      ip_address:   f.ip_address?.trim() || '000.000.00.000',
      device:       f.device?.trim() || 'Desktop',
      browser:      f.browser?.trim() || 'Chrome',
      notes:        f.notes?.trim() || '',
    };

    if (this.is_edit()) {
      this.doUpdate('activity_log', row);
      this.logs.update(list =>
        list.map(item => item.id === row.id ? row : item)
          .sort((a, b) => b.cur_date.localeCompare(a.cur_date)),
      );
      this.show_toast('Activity log updated.');
    } else {
      this.doSave('activity_log', row);
      this.logs.update(list => [row, ...list].sort((a, b) => b.cur_date.localeCompare(a.cur_date)));
      this.page.set(1);
      this.show_toast('Activity log added.');
    }
    this.close_form();
  }

  ask_delete(entry: activity_log_entry, ev?: Event): void {
    ev?.stopPropagation();
    this.selected_entry.set(null);
    this.delete_target.set(entry);
  }

  cancel_delete(): void {
    this.delete_target.set(null);
  }

  confirm_delete(): void {
    const target = this.delete_target();
    if (!target) return;

    this.doDelete('activity_log', target.id);

    this.logs.update(list => list.filter(row => row.id !== target.id));
    this.delete_target.set(null);
    this.show_toast('Activity log deleted.');
  }

  apply_user(user_id: string): void {
    const src = this.logs().find(r => r.user_id === user_id);
    if (!src) return;
    this.entry_form.user_id     = src.user_id;
    this.entry_form.user_name   = src.user_name;
    this.entry_form.user_role   = src.user_role;
    this.entry_form.user_email  = src.user_email;
    this.entry_form.user_avatar = src.user_avatar;
  }

  on_action_change(): void {
    if (this.entry_form.act_action) {
      this.entry_form.severity = this.default_severity(this.entry_form.act_action);
      if (this.entry_form.act_action === 'login_failed') this.entry_form.status = 'failed';
    }
  }

  export_csv(): void {
    const rows = this.filtered_logs();
    const header = [
      'ID', 'Timestamp', 'User', 'Role', 'Email', 'Action', 'Module',
      'Description', 'Entity', 'Entity ID', 'Severity', 'Status',
      'IP Address', 'Device', 'Browser',
    ];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = rows.map(r => [
      r.id, r.cur_date, r.user_name, r.user_role, r.user_email,
      r.act_action, r.act_module, r.description, r.entity_label, r.entity_id,
      r.severity, r.status, r.ip_address, r.device, r.browser,
    ].map(escape).join(','));
    const csv  = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `user-activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    this.show_toast('Activity log exported as CSV');
  }

  relative_time(iso: string): string {
    const diff = Date.now() - Date.parse(iso);
    const min  = Math.round(diff / 60000);
    if (min < 1)  return 'Just now';
    if (min < 60) return `${min} min ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  clock(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }

  action_icon(action: activity_action): string {
    const map: Record<activity_action, string> = {
      login:        'bi-box-arrow-in-right',
      logout:       'bi-box-arrow-right',
      login_failed: 'bi-shield-exclamation',
      create:       'bi-plus-circle',
      update:       'bi-pencil-square',
      delete:       'bi-trash3',
      view:         'bi-eye',
      export:       'bi-upload',
      approve:      'bi-check-circle',
      reject:       'bi-x-circle',
      print:        'bi-printer',
      download:     'bi-download',
      settings:     'bi-gear',
      permission:   'bi-key',
    };
    return map[action];
  }

  module_icon(module: activity_module): string {
    return this.module_opts.find(m => m.value === module)?.icon ?? 'bi-circle';
  }

  action_label(action: activity_action): string {
    return this.action_opts.find(a => a.value === action)?.label ?? action;
  }

  module_label(module: activity_module): string {
    return this.module_opts.find(m => m.value === module)?.label ?? module;
  }

  initials_color(avatar: string): string {
    const palette = ['#1a3a6b', '#0d9488', '#7c3aed', '#d97706', '#dc2626', '#0284c7', '#059669', '#be185d'];
    let hash = 0;
    for (const ch of avatar) hash = (hash + ch.charCodeAt(0)) % palette.length;
    return palette[hash];
  }

  private day_label(iso_date: string): string {
    const today = new Date().toISOString().slice(0, 10);
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const yesterday = y.toISOString().slice(0, 10);
    if (iso_date === today)     return 'Today';
    if (iso_date === yesterday) return 'Yesterday';
    return new Date(iso_date + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  private show_toast(msg: string): void {
    this.toast_msg.set(msg);
    setTimeout(() => this.toast_msg.set(''), 2800);
  }

  private next_id(): string {
    let max = 0;
    for (const row of this.logs()) {
      const n = Number(row.id.replace(/\D/g, ''));
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `act_${String(max + 1).padStart(4, '0')}`;
  }

  private slug_id(name: string): string {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'user';
    return `usr_${slug}`;
  }

  private make_avatar(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '??';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  private default_severity(action: activity_action): activity_severity {
    if (action === 'login_failed') return 'danger';
    if (action === 'delete' || action === 'permission' || action === 'reject') return 'warning';
    if (action === 'create' || action === 'approve' || action === 'login') return 'success';
    return 'info';
  }

  private to_local_input(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private from_local_input(value: string): string {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  }

  // private build_logs(): activity_log_entry[] {
  //   const office  = { ip_address: '103.21.244.12', device: 'Desktop', browser: 'Chrome 129' };
  //   const laptop  = { ip_address: '103.21.244.18', device: 'Laptop',  browser: 'Edge 128' };
  //   const mobile  = { ip_address: '49.36.112.88',  device: 'Mobile',  browser: 'Safari 18' };
  //   const unknown = { ip_address: '45.112.67.201', device: 'Unknown', browser: 'Unknown' };

  //   const admin  = { user_id: 'usr_001', user_name: 'Admin User',   user_role: 'Super Admin',        user_email: 'admin@edusmart.in',        user_avatar: 'AD' };
  //   const priya  = { user_id: 'usr_014', user_name: 'Priya Sharma', user_role: 'Senior Teacher',     user_email: 'priya.sharma@edusmart.in', user_avatar: 'PS' };
  //   const rajesh = { user_id: 'usr_022', user_name: 'Rajesh Kumar', user_role: 'Accountant',         user_email: 'rajesh.kumar@edusmart.in', user_avatar: 'RK' };
  //   const anita  = { user_id: 'usr_031', user_name: 'Anita Desai',  user_role: 'HR Manager',         user_email: 'anita.desai@edusmart.in',  user_avatar: 'An' };
  //   const suresh = { user_id: 'usr_044', user_name: 'Suresh Patel', user_role: 'Librarian',          user_email: 'suresh.patel@edusmart.in', user_avatar: 'SP' };
  //   const meena  = { user_id: 'usr_051', user_name: 'Meena Iyer',   user_role: 'Front Desk',         user_email: 'meena.iyer@edusmart.in',   user_avatar: 'MI' };
  //   const vikram = { user_id: 'usr_063', user_name: 'Vikram Singh', user_role: 'Transport Incharge', user_email: 'vikram.singh@edusmart.in', user_avatar: 'VS' };
  //   const guest  = { user_id: 'usr_000', user_name: 'Unknown',      user_role: 'Unauthenticated',    user_email: '',                         user_avatar: '??' };

  //   const rows: activity_log_entry[] = [
  //     { id:'act_1042', cur_date:'2026-09-21T09:48:00Z', ...admin,  ...office,  act_action:'view',         act_module:'dashboard',     description:'Opened the school dashboard',                              entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'info',    status:'success', notes:'' },
  //     { id:'act_1041', cur_date:'2026-09-21T09:47:12Z', ...admin,  ...office,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'method: password' },
  //     { id:'act_1040', cur_date:'2026-09-21T09:12:40Z', ...rajesh, ...laptop,  act_action:'export',       act_module:'fees',          description:'Exported September fee collection report (CSV)',            entity_type:'report',              entity_id:'rpt_fee_0926', entity_label:'Fee Collection – Sep 2026',         severity:'info',    status:'success', notes:'2847 rows' },
  //     { id:'act_1039', cur_date:'2026-09-21T08:55:03Z', ...meena,  ...office,  act_action:'create',       act_module:'admissions',    description:'Registered a new admission application',                    entity_type:'application',         entity_id:'app_2526_0048',entity_label:'Kavya Nair – Class VI',             severity:'success', status:'success', notes:'' },
  //     { id:'act_1038', cur_date:'2026-09-21T08:41:22Z', ...priya,  ...laptop,  act_action:'update',       act_module:'attendance',    description:'Marked Class XI-A attendance for 21 Sep 2026',              entity_type:'attendance_session',  entity_id:'att_110a_2109',entity_label:'XI-A · 21 Sep 2026',                severity:'info',    status:'success', notes:'present 38 · absent 2 · late 1' },
  //     { id:'act_1037', cur_date:'2026-09-21T08:02:11Z', ...meena,  ...office,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'' },
  //     { id:'act_1036', cur_date:'2026-09-21T07:58:44Z', ...priya,  ...laptop,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'' },
  //     { id:'act_1035', cur_date:'2026-09-21T07:51:09Z', ...rajesh, ...laptop,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'' },
  //     { id:'act_1034', cur_date:'2026-09-21T06:14:33Z', ...guest,  ...unknown, act_action:'login_failed', act_module:'auth',          description:'Failed sign-in attempt for admin@edusmart.in',              entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'danger',  status:'failed',  notes:'invalid_password' },
  //     { id:'act_1033', cur_date:'2026-09-20T16:42:00Z', ...admin,  ...office,  act_action:'logout',       act_module:'auth',          description:'Signed out of EduSmart ERP',                               entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'info',    status:'success', notes:'' },
  //     { id:'act_1032', cur_date:'2026-09-20T16:18:27Z', ...admin,  ...office,  act_action:'approve',      act_module:'gate-pass',     description:'Approved student gate pass GP-2026-118',                    entity_type:'gate_pass',           entity_id:'gp_2026_118',  entity_label:'Arjun Sharma · XI-A',               severity:'success', status:'success', notes:'Medical appointment · escort: Parent' },
  //     { id:'act_1031', cur_date:'2026-09-20T15:04:51Z', ...anita,  ...laptop,  act_action:'approve',      act_module:'hr',            description:'Approved leave application for Deepak Yadav',               entity_type:'leave',               entity_id:'lv_2026_094',  entity_label:'Deepak Yadav · Casual · 4 days',    severity:'success', status:'success', notes:'' },
  //     { id:'act_1030', cur_date:'2026-09-20T14:22:10Z', ...rajesh, ...laptop,  act_action:'create',       act_module:'finance',       description:'Recorded vendor payment for science lab supplies',          entity_type:'expense',             entity_id:'exp_2026_221', entity_label:'Lab Glassware – ₹18,400',           severity:'success', status:'success', notes:'' },
  //     { id:'act_1029', cur_date:'2026-09-20T12:47:33Z', ...suresh, ...office,  act_action:'create',       act_module:'library',       description:'Issued book “NCERT Physics Class XI” to student',           entity_type:'issue',               entity_id:'lib_iss_4412', entity_label:'Rohan Verma · X-B',                 severity:'info',    status:'success', notes:'' },
  //     { id:'act_1028', cur_date:'2026-09-20T11:15:08Z', ...admin,  ...office,  act_action:'update',       act_module:'staff',         description:'Updated designation for Mrs. Priya Sharma',                 entity_type:'staff',               entity_id:'stf_014',      entity_label:'Priya Sharma',                      severity:'info',    status:'success', notes:'Teacher → Senior Teacher' },
  //     { id:'act_1027', cur_date:'2026-09-20T10:03:41Z', ...vikram, ...mobile,  act_action:'update',       act_module:'transport',     description:'Updated insurance expiry reminder for bus RT-02',           entity_type:'vehicle',             entity_id:'veh_rt_02',    entity_label:'DL 02 CD 5678',                     severity:'warning', status:'success', notes:'' },
  //     { id:'act_1026', cur_date:'2026-09-20T09:01:55Z', ...admin,  ...office,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'' },
  //     { id:'act_1025', cur_date:'2026-09-19T17:30:12Z', ...priya,  ...laptop,  act_action:'create',       act_module:'exams',         description:'Published mid-term exam schedule for Class IX and X',       entity_type:'exam_schedule',       entity_id:'exm_mt_0926',  entity_label:'Mid-Term · Sep 2026',               severity:'success', status:'success', notes:'' },
  //     { id:'act_1024', cur_date:'2026-09-19T16:08:44Z', ...meena,  ...office,  act_action:'reject',       act_module:'eligibility',   description:'Eligibility check recorded as Not Eligible',                entity_type:'eligibility_check',   entity_id:'elg_2026_077', entity_label:'Rahul Mishra · Class XI Science',   severity:'warning', status:'success', notes:'verdict: Not Eligible' },
  //     { id:'act_1023', cur_date:'2026-09-19T15:22:01Z', ...meena,  ...office,  act_action:'create',       act_module:'eligibility',   description:'Ran eligibility check for Class XI – Science',              entity_type:'eligibility_check',   entity_id:'elg_2026_076', entity_label:'Sneha Patel · Class XI Commerce',   severity:'success', status:'success', notes:'verdict: Eligible' },
  //     { id:'act_1022', cur_date:'2026-09-19T13:40:18Z', ...rajesh, ...laptop,  act_action:'update',       act_module:'fees',          description:'Recorded fee payment of ₹18,000 (tuition)',                 entity_type:'payment',             entity_id:'pay_2026_1884',entity_label:'Ananya Kapoor · XII-A',             severity:'success', status:'success', notes:'UPI' },
  //     { id:'act_1021', cur_date:'2026-09-19T11:11:09Z', ...admin,  ...office,  act_action:'permission',   act_module:'settings',      description:'Granted Fee Management access to Rajesh Kumar',             entity_type:'role',                entity_id:'usr_022',      entity_label:'Rajesh Kumar',                      severity:'warning', status:'success', notes:'fees.write' },
  //     { id:'act_1020', cur_date:'2026-09-19T09:05:30Z', ...anita,  ...laptop,  act_action:'create',       act_module:'hr',            description:'Added job opening for Physics Teacher',                     entity_type:'job',                 entity_id:'job_2026_012', entity_label:'PGT Physics · 1 position',          severity:'success', status:'success', notes:'' },
  //     { id:'act_1019', cur_date:'2026-09-18T18:02:44Z', ...guest,  ...unknown, act_action:'login_failed', act_module:'auth',          description:'Failed sign-in attempt for rajesh.kumar@edusmart.in',       entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'danger',  status:'failed',  notes:'3 attempts' },
  //     { id:'act_1018', cur_date:'2026-09-18T16:45:00Z', ...suresh, ...office,  act_action:'update',       act_module:'library',       description:'Marked overdue return for “Wings of Fire”',                 entity_type:'issue',               entity_id:'lib_iss_4388', entity_label:'Dev Malhotra · IX-A',               severity:'warning', status:'success', notes:'6 days overdue · fine ₹30' },
  //     { id:'act_1017', cur_date:'2026-09-18T14:10:22Z', ...admin,  ...office,  act_action:'export',       act_module:'reports',       description:'Downloaded attendance below 75% report',                    entity_type:'report',              entity_id:'rpt_att_low',  entity_label:'Low Attendance – Sep 2026',         severity:'info',    status:'success', notes:'12 students' },
  //     { id:'act_1016', cur_date:'2026-09-18T12:28:55Z', ...vikram, ...mobile,  act_action:'create',       act_module:'transport',     description:'Assigned student to bus route RT-05',                       entity_type:'route_assignment',    entity_id:'tr_asn_902',   entity_label:'Pooja Nair · RT-05 Seat 14',        severity:'success', status:'success', notes:'' },
  //     { id:'act_1015', cur_date:'2026-09-18T10:40:07Z', ...anita,  ...laptop,  act_action:'update',       act_module:'hr',            description:'Processed September payroll for 134 staff',                 entity_type:'payroll',             entity_id:'payr_2026_09', entity_label:'Payroll · Sep 2026',                severity:'success', status:'success', notes:'' },
  //     { id:'act_1014', cur_date:'2026-09-17T17:12:39Z', ...admin,  ...office,  act_action:'delete',       act_module:'notifications', description:'Cleared 8 read system notifications',                       entity_type:'notification',        entity_id:'ntf_batch_11', entity_label:'8 notifications',                   severity:'warning', status:'success', notes:'' },
  //     { id:'act_1013', cur_date:'2026-09-17T15:33:18Z', ...meena,  ...office,  act_action:'print',        act_module:'gate-pass',     description:'Printed gate pass GP-2026-112',                             entity_type:'gate_pass',           entity_id:'gp_2026_112',  entity_label:'Sanya Iyer · XII-B',                severity:'info',    status:'success', notes:'' },
  //     { id:'act_1012', cur_date:'2026-09-17T13:05:47Z', ...priya,  ...laptop,  act_action:'update',       act_module:'timetable',     description:'Updated Class XI Science timetable for the new term',       entity_type:'timetable',           entity_id:'tt_xi_sci',    entity_label:'XI Science · 2025-26',              severity:'info',    status:'success', notes:'' },
  //     { id:'act_1011', cur_date:'2026-09-17T11:20:00Z', ...rajesh, ...laptop,  act_action:'download',     act_module:'fees',          description:'Downloaded payment receipt PAY-2026-1871',                  entity_type:'receipt',             entity_id:'pay_2026_1871',entity_label:'Rohan Verma · X-B',                 severity:'info',    status:'success', notes:'' },
  //     { id:'act_1010', cur_date:'2026-09-17T09:48:22Z', ...admin,  ...office,  act_action:'settings',     act_module:'subscription',  description:'Activated Fee Management add-on on Standard plan',          entity_type:'subscription',        entity_id:'sub_std',      entity_label:'Standard Plan',                     severity:'success', status:'success', notes:'' },
  //     { id:'act_1009', cur_date:'2026-09-16T16:55:03Z', ...suresh, ...office,  act_action:'create',       act_module:'library',       description:'Added 42 new titles to the catalogue',                      entity_type:'catalogue',           entity_id:'lib_cat_0916', entity_label:'New arrivals · Sep 2026',           severity:'success', status:'success', notes:'' },
  //     { id:'act_1008', cur_date:'2026-09-16T14:18:41Z', ...admin,  ...office,  act_action:'update',       act_module:'inventory',     description:'Approved sports department indent for footballs',           entity_type:'indent',              entity_id:'ind_004',      entity_label:'Sports Department · Football',      severity:'success', status:'success', notes:'' },
  //     { id:'act_1007', cur_date:'2026-09-16T12:02:15Z', ...meena,  ...office,  act_action:'create',       act_module:'directory',     description:'Added emergency contact for local police station',          entity_type:'contact',             entity_id:'dir_emg_08',   entity_label:'Police – Control Room',             severity:'success', status:'success', notes:'' },
  //     { id:'act_1006', cur_date:'2026-09-16T10:27:50Z', ...priya,  ...laptop,  act_action:'view',         act_module:'students',      description:'Opened student profile for Ananya Kapoor',                  entity_type:'student',             entity_id:'stu_1204',     entity_label:'Ananya Kapoor · XII-A',             severity:'info',    status:'success', notes:'' },
  //     { id:'act_1005', cur_date:'2026-09-16T08:44:19Z', ...guest,  ...unknown, act_action:'login_failed', act_module:'auth',          description:'Failed sign-in attempt for unknown account',                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'danger',  status:'failed',  notes:'user_not_found' },
  //     { id:'act_1004', cur_date:'2026-09-15T17:40:00Z', ...anita,  ...laptop,  act_action:'reject',       act_module:'hr',            description:'Rejected leave application for contract staff',             entity_type:'leave',               entity_id:'lv_2026_088',  entity_label:'Ravi Gupta · Unpaid · 2 days',      severity:'warning', status:'success', notes:'Insufficient notice period' },
  //     { id:'act_1003', cur_date:'2026-09-15T15:12:33Z', ...vikram, ...mobile,  act_action:'update',       act_module:'transport',     description:'Marked bus RT-07 as under maintenance',                     entity_type:'vehicle',             entity_id:'veh_rt_07',    entity_label:'DL 01 AB 3344',                     severity:'warning', status:'success', notes:'' },
  //     { id:'act_1002', cur_date:'2026-09-15T11:08:27Z', ...rajesh, ...laptop,  act_action:'create',       act_module:'fees',          description:'Created Sports & Activity fee head for 2025-26',            entity_type:'fee_head',            entity_id:'fh_spt',       entity_label:'Sports & Activity Fee',             severity:'success', status:'success', notes:'₹800 · optional' },
  //     { id:'act_1001', cur_date:'2026-09-15T09:22:14Z', ...admin,  ...office,  act_action:'settings',     act_module:'settings',      description:'Changed academic year lock to 2025–26',                     entity_type:'academic_year',       entity_id:'ay_2025_26',   entity_label:'2025–26',                           severity:'warning', status:'success', notes:'' },
  //     { id:'act_1000', cur_date:'2026-09-14T16:01:09Z', ...meena,  ...office,  act_action:'create',       act_module:'admissions',    description:'Registered a new admission application',                    entity_type:'application',         entity_id:'app_2526_0042',entity_label:'Arjun Sharma – Class XI Science',   severity:'success', status:'success', notes:'' },
  //     { id:'act_0999', cur_date:'2026-09-14T14:35:40Z', ...admin,  ...office,  act_action:'export',       act_module:'reports',       description:'Exported user activity log (CSV)',                          entity_type:'report',              entity_id:'rpt_act_1409', entity_label:'Activity Log · 1–14 Sep',           severity:'info',    status:'success', notes:'86 rows' },
  //     { id:'act_0998', cur_date:'2026-09-14T10:18:02Z', ...priya,  ...laptop,  act_action:'update',       act_module:'attendance',    description:'Corrected attendance for Rahul Mishra (was marked absent)', entity_type:'attendance_session',  entity_id:'att_110a_1409',entity_label:'XI-A · 14 Sep 2026',                severity:'warning', status:'success', notes:'' },
  //     { id:'act_0997', cur_date:'2026-09-14T08:05:55Z', ...admin,  ...office,  act_action:'login',        act_module:'auth',          description:'Signed in to EduSmart ERP',                                entity_type:'',                    entity_id:'',             entity_label:'',                                  severity:'success', status:'success', notes:'' },
  //   ];

  //   return rows.sort((a, b) => b.cur_date.localeCompare(a.cur_date));
  // }

  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
